import { useState, useEffect, useMemo } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Calendar as CalendarIcon, Plus, ChevronLeft, ChevronRight, Clock,
  MapPin, Users, Check, X, AlertTriangle, Settings, RefreshCw,
  UserPlus, HardHat, FileText, Truck, Wrench, GraduationCap, 
  Briefcase, User, MoreVertical, Edit, Trash2, Link2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

import { API_URL } from '../../config/api';

const ICON_MAP = {
  UserPlus: UserPlus,
  Users: Users,
  HardHat: HardHat,
  FileText: FileText,
  Truck: Truck,
  Wrench: Wrench,
  GraduationCap: GraduationCap,
  Briefcase: Briefcase,
  User: User,
  Calendar: CalendarIcon
};

const DAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const MONTHS = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
];

export default function SharedCalendarPage() {
  const { user, isAssistant } = useOutletContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [pendingEvents, setPendingEvents] = useState([]);
  const [categories, setCategories] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showConnectGoogleModal, setShowConnectGoogleModal] = useState(false);
  const [googleCalendarConnected, setGoogleCalendarConnected] = useState(false);
  const [view, setView] = useState('month'); // 'month' | 'week' | 'day'
  const [stats, setStats] = useState({ today: 0, this_week: 0, pending_approval: 0 });

  // Handle Google OAuth callback messages
  useEffect(() => {
    const success = searchParams.get('success');
    const error = searchParams.get('error');
    
    if (success === 'calendar_connected') {
      toast.success('Google Calendar connecté avec succès !');
      setSearchParams({});
      setGoogleCalendarConnected(true);
      fetchEvents();
    } else if (error) {
      toast.error(`Erreur de connexion: ${error}`);
      setSearchParams({});
    }
  }, [searchParams]);

  useEffect(() => {
    fetchCategories();
    fetchStats();
    checkGoogleCalendarConnection();
  }, []);

  useEffect(() => {
    fetchEvents();
    fetchPendingEvents();
  }, [currentDate]);

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/api/calendar/categories`);
      if (response.ok) {
        setCategories(await response.json());
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const checkGoogleCalendarConnection = async () => {
    const token = localStorage.getItem('client_token');
    try {
      const response = await fetch(`${API_URL}/api/calendar/accounts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const accounts = await response.json();
        const hasGoogle = accounts.some(acc => acc.provider === 'google' && acc.status === 'active');
        setGoogleCalendarConnected(hasGoogle);
      }
    } catch (error) {
      console.error('Error checking calendar connection:', error);
    }
  };

  const handleConnectGoogleCalendar = async () => {
    const token = localStorage.getItem('client_token');
    
    try {
      const response = await fetch(`${API_URL}/api/oauth/google/authorize?service=calendar`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Redirect to Google OAuth
        window.location.href = data.authorization_url;
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Erreur de connexion Google');
      }
    } catch (error) {
      toast.error('Erreur de connexion Google');
    }
  };

  const fetchEvents = async () => {
    const token = localStorage.getItem('client_token');
    setLoading(true);
    
    // Calculate date range for current month view
    const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
    
    try {
      const response = await fetch(
        `${API_URL}/api/calendar/events?start_date=${startOfMonth.toISOString()}&end_date=${endOfMonth.toISOString()}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        setEvents(await response.json());
      }
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingEvents = async () => {
    const token = localStorage.getItem('client_token');
    try {
      const response = await fetch(`${API_URL}/api/calendar/events/pending-approval`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setPendingEvents(await response.json());
      }
    } catch (error) {
      console.error('Error fetching pending events:', error);
    }
  };

  const fetchStats = async () => {
    const token = localStorage.getItem('client_token');
    try {
      const response = await fetch(`${API_URL}/api/calendar/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setStats(await response.json());
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleApproveEvent = async (eventId) => {
    const token = localStorage.getItem('client_token');
    try {
      const response = await fetch(`${API_URL}/api/calendar/events/${eventId}/approve`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('RDV approuvé !');
        fetchEvents();
        fetchPendingEvents();
        fetchStats();
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleRejectEvent = async (eventId) => {
    const token = localStorage.getItem('client_token');
    try {
      const response = await fetch(`${API_URL}/api/calendar/events/${eventId}/reject`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('RDV refusé');
        fetchEvents();
        fetchPendingEvents();
        fetchStats();
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    const token = localStorage.getItem('client_token');
    try {
      const response = await fetch(`${API_URL}/api/calendar/events/${eventId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Événement supprimé');
        fetchEvents();
        setSelectedEvent(null);
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  // Generate calendar grid
  const calendarDays = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);
    
    // Get the day of week (0 = Sunday, we want Monday = 0)
    let startDay = firstDayOfMonth.getDay() - 1;
    if (startDay === -1) startDay = 6;
    
    const daysInMonth = lastDayOfMonth.getDate();
    const days = [];
    
    // Previous month days
    const prevMonth = new Date(year, month, 0);
    for (let i = startDay - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, prevMonth.getDate() - i),
        isCurrentMonth: false
      });
    }
    
    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        date: new Date(year, month, i),
        isCurrentMonth: true
      });
    }
    
    // Next month days
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        date: new Date(year, month + 1, i),
        isCurrentMonth: false
      });
    }
    
    return days;
  }, [currentDate]);

  const getEventsForDate = (date) => {
    return events.filter(event => {
      const eventDate = new Date(event.start_time);
      return eventDate.toDateString() === date.toDateString();
    });
  };

  const navigateMonth = (direction) => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + direction, 1));
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="space-y-4" data-testid="shared-calendar-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <CalendarIcon className="w-6 h-6 text-rm-primary" />
          <h1 className="text-xl font-bold text-rm-text">Agenda partagé</h1>
        </div>
        <div className="flex items-center gap-2">
          {!isAssistant && !googleCalendarConnected && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleConnectGoogleCalendar}
              className="gap-2"
              data-testid="connect-google-calendar-btn"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Google Calendar
            </Button>
          )}
          {googleCalendarConnected && (
            <span className="text-xs text-green-600 flex items-center gap-1 px-2 py-1 bg-green-50 rounded-full">
              <Check className="w-3 h-3" />
              Google connecté
            </span>
          )}
          <Button variant="outline" size="sm" onClick={fetchEvents}>
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button size="sm" onClick={() => setShowCreateModal(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Nouveau RDV
          </Button>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white rounded-lg border border-slate-200 p-3">
          <p className="text-2xl font-bold text-rm-primary">{stats.today}</p>
          <p className="text-xs text-slate-500">Aujourd'hui</p>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-3">
          <p className="text-2xl font-bold text-blue-600">{stats.this_week}</p>
          <p className="text-xs text-slate-500">Cette semaine</p>
        </div>
        {!isAssistant && stats.pending_approval > 0 && (
          <div className="bg-amber-50 rounded-lg border border-amber-200 p-3">
            <p className="text-2xl font-bold text-amber-600">{stats.pending_approval}</p>
            <p className="text-xs text-amber-600">À valider</p>
          </div>
        )}
      </div>

      {/* Pending Approval Banner */}
      {!isAssistant && pendingEvents.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-amber-50 border border-amber-200 rounded-xl p-4"
        >
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            <h3 className="font-semibold text-amber-800">
              {pendingEvents.length} RDV en attente de validation
            </h3>
          </div>
          <div className="space-y-2">
            {pendingEvents.slice(0, 3).map(event => {
              const catInfo = categories[event.category] || {};
              return (
                <div key={event.id} className="flex items-center justify-between bg-white rounded-lg p-3 border border-amber-100">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: catInfo.color || '#6B7280' }}
                    />
                    <div>
                      <p className="font-medium text-sm">{event.title}</p>
                      <p className="text-xs text-slate-500">
                        {new Date(event.start_time).toLocaleDateString('fr-FR')} à {formatTime(event.start_time)}
                        {event.created_by_name && ` • Par ${event.created_by_name}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button 
                      size="sm" 
                      variant="outline"
                      className="text-red-600 border-red-200 hover:bg-red-50"
                      onClick={() => handleRejectEvent(event.id)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                    <Button 
                      size="sm"
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() => handleApproveEvent(event.id)}
                    >
                      <Check className="w-4 h-4 mr-1" />
                      Valider
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>
      )}

      {/* Calendar */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {/* Month Navigation */}
        <div className="flex items-center justify-between p-4 border-b border-slate-100">
          <button
            onClick={() => navigateMonth(-1)}
            className="p-2 hover:bg-slate-100 rounded-lg"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h2 className="text-lg font-semibold text-rm-text">
            {MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}
          </h2>
          <button
            onClick={() => navigateMonth(1)}
            className="p-2 hover:bg-slate-100 rounded-lg"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* Days Header */}
        <div className="grid grid-cols-7 border-b border-slate-100">
          {DAYS.map(day => (
            <div key={day} className="py-2 text-center text-sm font-medium text-slate-500">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7">
          {calendarDays.map((day, index) => {
            const dayEvents = getEventsForDate(day.date);
            const isSelected = selectedDate?.toDateString() === day.date.toDateString();
            
            return (
              <div
                key={index}
                onClick={() => setSelectedDate(day.date)}
                className={`min-h-[100px] p-1 border-b border-r border-slate-100 cursor-pointer transition-colors ${
                  !day.isCurrentMonth ? 'bg-slate-50' : 'hover:bg-slate-50'
                } ${isSelected ? 'bg-rm-primary/5' : ''}`}
              >
                <div className={`text-sm font-medium mb-1 w-7 h-7 flex items-center justify-center rounded-full ${
                  isToday(day.date) ? 'bg-rm-primary text-white' : 
                  !day.isCurrentMonth ? 'text-slate-400' : 'text-slate-700'
                }`}>
                  {day.date.getDate()}
                </div>
                <div className="space-y-1">
                  {dayEvents.slice(0, 3).map(event => {
                    const catInfo = categories[event.category] || {};
                    return (
                      <div
                        key={event.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedEvent(event);
                        }}
                        className={`text-xs px-1.5 py-0.5 rounded truncate cursor-pointer hover:opacity-80 ${
                          event.status === 'pending_approval' ? 'border border-amber-300 bg-amber-50 text-amber-700' : ''
                        }`}
                        style={{ 
                          backgroundColor: event.status !== 'pending_approval' ? `${catInfo.color}20` : undefined,
                          color: event.status !== 'pending_approval' ? catInfo.color : undefined
                        }}
                      >
                        {event.title}
                      </div>
                    );
                  })}
                  {dayEvents.length > 3 && (
                    <div className="text-xs text-slate-400 px-1">
                      +{dayEvents.length - 3} autres
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3">
        {Object.entries(categories).slice(0, 6).map(([key, cat]) => (
          <div key={key} className="flex items-center gap-1.5 text-xs text-slate-600">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: cat.color }} />
            {cat.label}
          </div>
        ))}
      </div>

      {/* Event Detail Modal */}
      <AnimatePresence>
        {selectedEvent && (
          <EventDetailModal
            event={selectedEvent}
            category={categories[selectedEvent.category]}
            isAssistant={isAssistant}
            onClose={() => setSelectedEvent(null)}
            onApprove={() => handleApproveEvent(selectedEvent.id)}
            onReject={() => handleRejectEvent(selectedEvent.id)}
            onDelete={() => handleDeleteEvent(selectedEvent.id)}
          />
        )}
      </AnimatePresence>

      {/* Create Event Modal */}
      {showCreateModal && (
        <CreateEventModal
          categories={categories}
          isAssistant={isAssistant}
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            setShowCreateModal(false);
            fetchEvents();
            fetchStats();
            if (isAssistant) {
              toast.success('RDV envoyé pour validation');
            } else {
              toast.success('Événement créé');
            }
          }}
          initialDate={selectedDate}
        />
      )}
    </div>
  );
}

// Event Detail Modal
function EventDetailModal({ event, category, isAssistant, onClose, onApprove, onReject, onDelete }) {
  const Icon = ICON_MAP[category?.icon] || CalendarIcon;
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.95 }}
        onClick={e => e.stopPropagation()}
        className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden"
      >
        <div 
          className="p-4 text-white"
          style={{ backgroundColor: category?.color || '#3B82F6' }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Icon className="w-5 h-5" />
              <span className="text-sm opacity-90">{category?.label || 'Événement'}</span>
            </div>
            <button onClick={onClose} className="p-1 hover:bg-white/20 rounded">
              <X className="w-5 h-5" />
            </button>
          </div>
          <h3 className="text-xl font-semibold mt-2">{event.title}</h3>
        </div>
        
        <div className="p-4 space-y-4">
          <div className="flex items-center gap-3 text-slate-600">
            <Clock className="w-5 h-5" />
            <div>
              <p className="font-medium">
                {new Date(event.start_time).toLocaleDateString('fr-FR', { 
                  weekday: 'long', day: 'numeric', month: 'long' 
                })}
              </p>
              <p className="text-sm">
                {new Date(event.start_time).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                {' - '}
                {new Date(event.end_time).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
          </div>
          
          {event.location && (
            <div className="flex items-center gap-3 text-slate-600">
              <MapPin className="w-5 h-5" />
              <span>{event.location}</span>
            </div>
          )}
          
          {event.description && (
            <p className="text-slate-600 text-sm bg-slate-50 p-3 rounded-lg">
              {event.description}
            </p>
          )}
          
          {event.created_by_name && (
            <p className="text-xs text-slate-400">
              Créé par {event.created_by_name}
            </p>
          )}
          
          {event.status === 'pending_approval' && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm text-amber-700 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                En attente de validation
              </p>
            </div>
          )}
        </div>
        
        <div className="p-4 border-t border-slate-100 flex justify-between">
          {event.status === 'pending_approval' && !isAssistant ? (
            <div className="flex gap-2">
              <Button variant="outline" className="text-red-600" onClick={onReject}>
                <X className="w-4 h-4 mr-1" />
                Refuser
              </Button>
              <Button className="bg-green-600 hover:bg-green-700" onClick={onApprove}>
                <Check className="w-4 h-4 mr-1" />
                Valider
              </Button>
            </div>
          ) : (
            <Button variant="outline" className="text-red-600" onClick={onDelete}>
              <Trash2 className="w-4 h-4 mr-1" />
              Supprimer
            </Button>
          )}
          <Button variant="outline" onClick={onClose}>Fermer</Button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// Create Event Modal
function CreateEventModal({ categories, isAssistant, onClose, onCreated, initialDate }) {
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('rdv_prospect');
  const [date, setDate] = useState(initialDate ? initialDate.toISOString().split('T')[0] : new Date().toISOString().split('T')[0]);
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('10:00');
  const [location, setLocation] = useState('');
  const [description, setDescription] = useState('');
  const [creating, setCreating] = useState(false);

  const handleCreate = async () => {
    if (!title) {
      toast.error('Titre requis');
      return;
    }

    const token = localStorage.getItem('client_token');
    setCreating(true);

    const startDateTime = new Date(`${date}T${startTime}`);
    const endDateTime = new Date(`${date}T${endTime}`);

    try {
      const response = await fetch(`${API_URL}/api/calendar/events`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title,
          category,
          start_time: startDateTime.toISOString(),
          end_time: endDateTime.toISOString(),
          location: location || undefined,
          description: description || undefined
        })
      });

      if (response.ok) {
        onCreated();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur de création');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden"
      >
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-semibold text-rm-text flex items-center gap-2">
            <CalendarIcon className="w-5 h-5 text-rm-primary" />
            Nouveau RDV
          </h3>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4 max-h-[60vh] overflow-y-auto">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Titre *</label>
            <input
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="Ex: RDV M. Dupont - Devis cuisine"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-rm-primary"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Catégorie</label>
            <div className="grid grid-cols-3 gap-2">
              {Object.entries(categories).map(([key, cat]) => (
                <button
                  key={key}
                  onClick={() => setCategory(key)}
                  className={`p-2 rounded-lg border text-xs text-left transition-colors ${
                    category === key 
                      ? 'border-2' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                  style={{ 
                    borderColor: category === key ? cat.color : undefined,
                    backgroundColor: category === key ? `${cat.color}10` : undefined
                  }}
                >
                  <div 
                    className="w-3 h-3 rounded-full mb-1" 
                    style={{ backgroundColor: cat.color }}
                  />
                  {cat.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Date</label>
              <input
                type="date"
                value={date}
                onChange={e => setDate(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Début</label>
              <input
                type="time"
                value={startTime}
                onChange={e => setStartTime(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Fin</label>
              <input
                type="time"
                value={endTime}
                onChange={e => setEndTime(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Lieu</label>
            <input
              type="text"
              value={location}
              onChange={e => setLocation(e.target.value)}
              placeholder="Adresse ou lieu du RDV"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Notes ou détails..."
              rows={3}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg resize-none"
            />
          </div>

          {isAssistant && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm text-amber-700 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Ce RDV sera envoyé au client pour validation
              </p>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-slate-100 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={handleCreate} disabled={creating}>
            {creating ? (
              <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white" />
            ) : (
              <>
                <Plus className="w-4 h-4 mr-2" />
                {isAssistant ? 'Demander validation' : 'Créer'}
              </>
            )}
          </Button>
        </div>
      </motion.div>
    </div>
  );
}
